import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageContainer } from '../components/layout/PageContainer';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { StatusBadge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { Modal, ConfirmModal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';
import { Textarea } from '../components/ui/Textarea';
import {
  getSiteDetails,
  approveSite,
  requestRevision,
  impersonateCustomer,
  markSiteLive,
  type InternalQADetailsResponse,
} from '../api/internal-qa';
import { processRevision, setRevisionStatus, type ManualRevisionStatus } from '../api/revisions';
import {
  getWebsiteImages,
  getImages,
  assignWebsiteImage,
  type CachedImage,
  type WebsiteImageSlot,
  type ImageType,
  INDUSTRIES,
  IMAGE_TYPES,
} from '../api/stock-images';
import { formatRelativeTime } from '../utils/formatters';
import { SECTION_OPTIONS } from '../utils/constants';
import { DomainManagement } from '../components/DomainManagement';
import { DomainDelegation } from '../components/DomainDelegation';
import { SiteImageImport } from '../components/SiteImageImport';
import { EmailForwardingManager } from '../components/EmailForwardingManager';
import { DnsRecordsManager } from '../components/DnsRecordsManager';
import { EmailForwardingToggle } from '../components/EmailForwardingToggle';
import { SiteSettings } from '../components/SiteSettings';
import { LeadsPanel } from '../components/LeadsPanel';
import { OnboardingDetails } from '../components/OnboardingDetails';

type ViewportSize = 'desktop' | 'tablet' | 'mobile';

// Zoom range constants
const MIN_ZOOM = 25;
const MAX_ZOOM = 200;
const ZOOM_STEP = 5;

// Right panel width
const PANEL_WIDTH = 384; // 96 * 4 = w-96 in tailwind

/**
 * The site's stable address, not the deployment it was first published to.
 *
 * `cloudflare_url` is stored as `https://<hash>.<project>.pages.dev` from the
 * first deployment, and every deployment keeps its own address, so framing it
 * showed the site as it was on day one: a changed number kept reading 500 after
 * the live site said 700. The project address (the hash removed) always serves
 * the latest production deployment.
 */
function stableSiteUrl(url: string): string {
  return url.replace(/^(https?:\/\/)[0-9a-f]{8}\.([^/]+\.pages\.dev)/i, '$1$2');
}

export function SiteDetailPage() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = useState<InternalQADetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewport, setViewport] = useState<ViewportSize>('desktop');
  const [zoomLevel, setZoomLevel] = useState<number>(100);

  // Right panel drawer state
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem('siteDetailPanelOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isPanelHovering, setIsPanelHovering] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Preview container ref for calculating fit zoom
  const previewContainerRef = useRef<HTMLDivElement>(null);

  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showMakeLiveModal, setShowMakeLiveModal] = useState(false);
  const [showLaunchNowModal, setShowLaunchNowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  // Revision form state
  const [statusRevisionId, setStatusRevisionId] = useState<string | null>(null);
  const [revisionSection, setRevisionSection] = useState('');
  const [revisionDescription, setRevisionDescription] = useState('');
  const [processingRevisionId, setProcessingRevisionId] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<'details' | 'onboarding' | 'images' | 'domain' | 'leads' | 'settings'>('details');
  const [websiteImages, setWebsiteImages] = useState<WebsiteImageSlot[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState<string | null>(null); // slot name
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerImageType, setPickerImageType] = useState<string>('');
  const [pickerIndustry, setPickerIndustry] = useState<string>('');
  const [pickerImages, setPickerImages] = useState<CachedImage[]>([]);

  useEffect(() => {
    if (!websiteId) return;

    async function fetchDetails() {
      try {
        const data = await getSiteDetails(websiteId!);
        setDetails(data);
      } catch (err) {
        setError('Failed to load site details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchDetails();
  }, [websiteId]);

  // Fetch website images when Images tab is selected
  useEffect(() => {
    if (!websiteId || activeTab !== 'images') return;

    async function fetchImages() {
      setImagesLoading(true);
      try {
        const websiteImagesRes = await getWebsiteImages(websiteId!);
        setWebsiteImages(websiteImagesRes.images);
      } catch (err) {
        console.error('Failed to load images:', err);
      } finally {
        setImagesLoading(false);
      }
    }

    fetchImages();
  }, [websiteId, activeTab]);

  // Fetch filtered images for the picker modal
  useEffect(() => {
    if (!showImagePicker) return;

    async function fetchPickerImages() {
      setPickerLoading(true);
      try {
        const filters: {
          reviewStatus: 'approved';
          limit: number;
          industry?: string;
          imageType?: ImageType;
        } = {
          reviewStatus: 'approved',
          limit: 100,
        };

        if (pickerIndustry) {
          filters.industry = pickerIndustry;
        }
        if (pickerImageType) {
          filters.imageType = pickerImageType as ImageType;
        }

        const res = await getImages(filters);
        setPickerImages(res.images);
      } catch (err) {
        console.error('Failed to load picker images:', err);
      } finally {
        setPickerLoading(false);
      }
    }

    fetchPickerImages();
  }, [showImagePicker, pickerIndustry, pickerImageType]);

  // Initialize picker filters based on the slot being edited
  const openImagePicker = useCallback((slot: string) => {
    // Don't pre-filter by image type - let user see all images first
    // They can filter down if needed
    setPickerImageType('');
    setPickerIndustry('');
    setShowImagePicker(slot);
  }, []);

  // Persist panel open state
  useEffect(() => {
    localStorage.setItem('siteDetailPanelOpen', JSON.stringify(isPanelOpen));
  }, [isPanelOpen]);

  // Handle hover interactions for drawer
  const handlePanelMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (!isPanelOpen) {
      setIsPanelHovering(true);
    }
  }, [isPanelOpen]);

  const handlePanelMouseLeave = useCallback(() => {
    if (!isPanelOpen) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsPanelHovering(false);
      }, 150); // Small delay to prevent flickering
    }
  }, [isPanelOpen]);

  // Open panel (click mode - pushes content)
  const handlePanelOpen = useCallback(() => {
    setIsPanelOpen(true);
    setIsPanelHovering(false);
  }, []);

  // Close panel
  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
    setIsPanelHovering(false);
  }, []);

  // Base viewport dimensions (defined here so it's available for calculateFitZoom)
  const viewportDimensions: Record<ViewportSize, { width: number; height: number }> = {
    desktop: { width: 1440, height: 900 },
    tablet: { width: 768, height: 1024 },
    mobile: { width: 375, height: 667 },
  };

  // Calculate zoom to fit viewport width in container
  const calculateFitZoom = useCallback(() => {
    if (!previewContainerRef.current) return 100;
    // Get the actual visible width of the container (getBoundingClientRect gives us the rendered size)
    const rect = previewContainerRef.current.getBoundingClientRect();
    const containerWidth = rect.width - 32; // Account for padding
    const { width } = viewportDimensions[viewport];
    // Calculate zoom needed to fit the viewport width into the container
    // This works for both scaling up (small viewport) and scaling down (large viewport)
    const fitZoom = (containerWidth / width) * 100;
    // Clamp to valid range and round to nearest step
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.round(fitZoom / ZOOM_STEP) * ZOOM_STEP));
  }, [viewport, isPanelOpen]); // Include isPanelOpen so it recalculates when panel changes

  const handleFitZoom = useCallback(() => {
    // Use requestAnimationFrame to ensure layout is updated before calculating
    requestAnimationFrame(() => {
      const newZoom = calculateFitZoom();
      setZoomLevel(newZoom);
    });
  }, [calculateFitZoom]);

  const handleApprove = async () => {
    if (!websiteId) return;
    setActionLoading(true);
    try {
      await approveSite(websiteId);
      setShowApproveModal(false);
      navigate('/internal-qa');
    } catch (err) {
      // Surface the server's reason. It explains *why* a transition was
      // refused (e.g. wrong current status); a generic message sends people
      // hunting for a bug that isn't there.
      setError(err instanceof Error ? err.message : 'Failed to approve site');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevision = async () => {
    if (!websiteId || !revisionDescription.trim()) return;
    setActionLoading(true);
    try {
      await requestRevision(websiteId, {
        section: revisionSection || undefined,
        description: revisionDescription.trim(),
      });
      setShowRevisionModal(false);
      setRevisionSection('');
      setRevisionDescription('');
      // Refresh details
      const data = await getSiteDetails(websiteId);
      setDetails(data);
    } catch (err) {
      setError('Failed to submit revision request');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  // Admin override: launch the site live without waiting for customer approval.
  // Reuses the same Make Live pipeline; the backend relaxes the status guard
  // when override is set (only from post-internal-QA statuses).
  const handleLaunchNow = async () => {
    if (!websiteId) return;
    setActionLoading(true);
    try {
      await markSiteLive(websiteId, { override: true, markedBy: 'admin-override' });
      setShowLaunchNowModal(false);
      navigate('/internal-qa');
    } catch (err) {
      setError('Failed to launch site');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMakeLive = async () => {
    if (!websiteId) return;
    setActionLoading(true);
    try {
      await markSiteLive(websiteId);
      setShowMakeLiveModal(false);
      navigate('/internal-qa');
    } catch (err) {
      setError('Failed to mark site as live');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  // Move a request through the workflow by hand. Distinct from "Process with
  // AI" — this only records where the work stands, it does not touch the site.
  // The AI revision processor reads site files from GitHub. V4 sites have no
  // repo (content lives in D1), so it can never succeed for them.
  const isV4Site = details?.website?.generator_version === 'v4';
  const isExternalSite = details?.website?.management === 'external';

  const handleSetRevisionStatus = async (
    revisionId: string,
    status: ManualRevisionStatus
  ) => {
    let note: string | undefined;

    if (status === 'rejected') {
      // The customer is shown this beside "Not being made". Declining without
      // a reason is worse for them than no answer, so require it here as well
      // as server-side.
      const reason = window.prompt(
        'Why is this change not being made?\n\nThe customer sees this in their portal.'
      );
      if (reason === null) return;
      if (!reason.trim()) {
        setError('A reason is required when declining a request.');
        return;
      }
      note = reason.trim();
    }

    setStatusRevisionId(revisionId);
    setError(null);
    try {
      await setRevisionStatus(revisionId, status, note);
      // Refresh so the row reflects the new status and note
      const data = await getSiteDetails(websiteId!);
      setDetails(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update revision status');
    } finally {
      setStatusRevisionId(null);
    }
  };

  const handleProcessRevision = async (revisionId: string) => {
    if (!websiteId) return;
    setProcessingRevisionId(revisionId);
    try {
      await processRevision(revisionId);
      // Refresh details to show updated revision status
      const data = await getSiteDetails(websiteId);
      setDetails(data);
      // Refresh iframe after processing
      setTimeout(() => {
        const iframe = document.querySelector('iframe');
        if (iframe) {
          iframe.src = iframe.src;
        }
      }, 3000);
    } catch (err) {
      setError('Failed to process revision');
      console.error(err);
    } finally {
      setProcessingRevisionId(null);
    }
  };

  const handleAssignImage = async (imageId: string) => {
    if (!websiteId || !showImagePicker) return;
    try {
      await assignWebsiteImage(websiteId, imageId, showImagePicker);
      // Refresh website images
      const res = await getWebsiteImages(websiteId);
      setWebsiteImages(res.images);
      setShowImagePicker(null);
    } catch (err) {
      console.error('Failed to assign image:', err);
    }
  };

  const handleImpersonate = async () => {
    if (!details?.website.customer_id) return;
    setImpersonateLoading(true);
    try {
      const result = await impersonateCustomer(details.website.customer_id);
      // Open the portal URL in a new tab
      window.open(result.portalUrl, '_blank');
    } catch (err) {
      console.error('Failed to impersonate customer:', err);
      setError('Failed to open customer portal');
    } finally {
      setImpersonateLoading(false);
    }
  };

  // Calculate scaled dimensions and styles
  const getViewportStyles = () => {
    const scale = zoomLevel / 100;
    const { width, height } = viewportDimensions[viewport];
    const scaledWidth = width * scale;
    const scaledHeight = height * scale;

    return {
      container: viewport === 'desktop' ? 'w-full' : 'flex justify-center',
      iframe: {
        width: `${width}px`,
        height: `${height}px`,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      },
      wrapper: {
        width: `${scaledWidth}px`,
        minWidth: `${scaledWidth}px`,
        height: `${scaledHeight}px`,
        overflow: 'hidden',
      },
    };
  };

  const styles = getViewportStyles();

  if (loading) {
    return (
      <PageContainer title="Site Review">
        <div className="flex items-center justify-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  if (error || !details) {
    return (
      <PageContainer title="Site Review">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error || 'Site not found'}
        </div>
      </PageContainer>
    );
  }

  const { website, onboarding, techQAResult, revisions } = details;

  // Once the custom domain is connected it IS the site's address; the pages.dev
  // subdomain is scaffolding. Leading with the subdomain after the domain went
  // live answers "where does this site live" with the least useful true answer.
  const liveUrl =
    website.custom_domain && website.domain_verification_status === 'connected'
      ? `https://${website.custom_domain}`
      : website.cloudflare_url || undefined;

  return (
    <PageContainer
      title={website.business_name || 'Site Review'}
      subtitle={liveUrl}
      action={
        <div className="flex gap-3">
          {!isExternalSite && (
            <Button
              variant="outline"
              onClick={() => navigate(`/editor/${websiteId}`)}
            >
              Edit Site
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleImpersonate}
            loading={impersonateLoading}
          >
            View as Customer
          </Button>
          {(website.status === 'pending-customer-review' ||
            website.status === 'needs-customer-revision') && (
            <Button
              variant="danger"
              onClick={() => setShowLaunchNowModal(true)}
            >
              Launch Now
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => setShowRevisionModal(true)}
          >
            Request Revision
          </Button>
          <Button onClick={() => setShowApproveModal(true)}>
            Send for Customer Review
          </Button>
          {website.status === 'approved-to-launch' && (
            <Button
              variant="primary"
              onClick={() => setShowMakeLiveModal(true)}
            >
              Make Live
            </Button>
          )}
        </div>
      }
    >
      <div className="flex gap-6 relative">
        {/* Left Column: Preview - expands when panel is closed */}
        <div
          ref={previewContainerRef}
          className="flex-1 space-y-4 transition-all duration-300 ease-in-out"
          style={{ marginRight: isPanelOpen ? `${PANEL_WIDTH}px` : 0 }}
        >
          {/* Viewport & Zoom Controls */}
          <Card>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Device:</span>
              <div className="flex gap-1">
                {(['desktop', 'tablet', 'mobile'] as ViewportSize[]).map((size) => (
                  <button
                    key={size}
                    onClick={() => setViewport(size)}
                    className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1.5 ${
                      viewport === size
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    title={`${size.charAt(0).toUpperCase() + size.slice(1)} view (${viewportDimensions[size].width}x${viewportDimensions[size].height})`}
                  >
                    {size === 'desktop' && (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    )}
                    {size === 'tablet' && (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    )}
                    {size === 'mobile' && (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    )}
                    {size.charAt(0).toUpperCase() + size.slice(1)}
                  </button>
                ))}
              </div>

              {/* Zoom Controls */}
              <div className="flex items-center gap-3 ml-4 border-l pl-4 border-gray-200">
                <span className="text-sm font-medium text-gray-700">Zoom:</span>
                <div className="flex items-center gap-2">
                  {/* Fit button */}
                  <button
                    onClick={handleFitZoom}
                    className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    title="Fit to viewport width"
                  >
                    Fit
                  </button>
                  {/* Zoom slider */}
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={MIN_ZOOM}
                      max={MAX_ZOOM}
                      step={ZOOM_STEP}
                      value={zoomLevel}
                      onChange={(e) => setZoomLevel(Number(e.target.value))}
                      className="w-24 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                      title={`Zoom: ${zoomLevel}%`}
                    />
                    <span className="text-xs font-medium text-gray-600 w-10 text-right">{zoomLevel}%</span>
                  </div>
                  {/* 100% button */}
                  <button
                    onClick={() => setZoomLevel(100)}
                    className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                      zoomLevel === 100
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    title="Reset to 100%"
                  >
                    100%
                  </button>
                </div>
              </div>

              <div className="flex-1" />

              {/* `status` describes the SITE, not the customer's domain, and the
                  two diverge for most of a BYOD launch: the site is genuinely
                  live on its pages.dev subdomain while the custom domain still
                  points at the old host. An unqualified "Live" reads as "their
                  domain is up", which is the same shape of overstatement that
                  let a domain sit reported as connected while it served Wix. */}
              <div className="flex flex-col items-end gap-1">
                <StatusBadge status={website.status} />
                {website.status === 'live' &&
                  website.custom_domain &&
                  website.domain_verification_status !== 'connected' && (
                    <span className="text-xs text-amber-600 whitespace-nowrap">
                      on the niftee subdomain · {website.custom_domain} not connected yet
                    </span>
                  )}
              </div>
            </div>
          </Card>

          {/* Site Preview */}
          <Card padding="none" className="overflow-auto">
            <div className="bg-gray-100 p-4">
              {website.cloudflare_url ? (
                <div className={styles.container}>
                  <div
                    style={styles.wrapper}
                    className="relative border rounded-lg bg-white overflow-hidden"
                  >
                    <iframe
                      src={stableSiteUrl(website.cloudflare_url)}
                      style={styles.iframe}
                      className="border-0"
                      title="Site Preview"
                    />
                  </div>
                </div>
              ) : (
                <div className="h-[600px] flex items-center justify-center text-gray-500">
                  No preview URL available
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Right edge hover zone (when panel is closed) - covers full height and edge */}
        {!isPanelOpen && !isPanelHovering && (
          <div
            onMouseEnter={handlePanelMouseEnter}
            className="fixed top-0 h-screen cursor-pointer"
            style={{ right: 0, width: '20px', zIndex: 9998 }}
            title="Hover to open details panel"
          />
        )}

        {/* Panel Toggle Button (when panel is closed) */}
        {!isPanelOpen && !isPanelHovering && (
          <button
            ref={triggerRef}
            onMouseEnter={handlePanelMouseEnter}
            onClick={handlePanelOpen}
            className="fixed right-0 top-1/2 -translate-y-1/2 bg-white border border-r-0 border-gray-200 rounded-l-lg p-2 shadow-md hover:bg-gray-50 transition-colors"
            style={{ zIndex: 9998 }}
            title="Open details panel"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Right Column: Slide-out Drawer */}
        <div
          ref={panelRef}
          onMouseEnter={handlePanelMouseEnter}
          onMouseLeave={handlePanelMouseLeave}
          className={`fixed top-0 h-screen bg-white border-l border-gray-200 shadow-lg flex flex-col transition-transform duration-300 ease-in-out ${
            isPanelOpen || isPanelHovering ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{
            width: `${PANEL_WIDTH}px`,
            right: 0,
            zIndex: 9999, // High z-index to ensure it's above everything including scrollbars
          }}
        >
          {/* Panel Header with Close Button */}
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <span className="font-semibold text-gray-900">Site Details</span>
            {isPanelOpen && (
              <button
                onClick={handlePanelClose}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                title="Close panel"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {!isPanelOpen && isPanelHovering && (
              <button
                onClick={handlePanelOpen}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                title="Pin panel open"
              >
                Pin open
              </button>
            )}
          </div>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Tab Switcher */}
            <div className="flex gap-2 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('onboarding')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'onboarding'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Onboarding
            </button>
            <button
              onClick={() => setActiveTab('images')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'images'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Images
            </button>
            <button
              onClick={() => setActiveTab('domain')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'domain'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Domain
            </button>
            <button
              onClick={() => setActiveTab('leads')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'leads'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Leads
            </button>
            {/*
              * Settings is a V2-only tab.
              *
              * Every control in it feeds the legacy generator and takes effect
              * only on regeneration: Style Recipe is labelled "for V2-generated
              * sites", Hero Style and Brand Colors both say "requires
              * regeneration". V4 sites cannot be regenerated — it would rebuild
              * them from onboarding data and discard the site — so all of it is
              * inert there, and the regenerate button was actively dangerous.
              *
              * Brand colours on a V4 site are staff-editable in the EDITOR,
              * which writes CSS variables and triggers a scaffold rebuild. That
              * is the live path; this tab is not.
              */}
            {!isV4Site && (
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'settings'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Settings
              </button>
            )}
          </div>

          {/* Images Tab Content */}
          {activeTab === 'images' && websiteId && (
            <div className="mb-6">
              {/* Harvesting from the old site is a precursor to assigning slots,
                  so it sits above the slot list rather than below it. */}
              <SiteImageImport
                websiteId={websiteId}
                suggestedUrl={website.custom_domain ? `https://${website.custom_domain}` : null}
              />
            </div>
          )}

          {activeTab === 'images' && (
            <Card>
              <h3 className="font-semibold text-gray-900 mb-4">Site Images</h3>
              {imagesLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : websiteImages.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">No images assigned to this site yet.</p>
                  <p className="text-sm text-gray-400">
                    Images will be automatically assigned during site generation,
                    or you can manually assign them below.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {websiteImages.map((slot) => (
                    <div key={slot.slot} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      {slot.image ? (
                        <img
                          src={slot.image.r2_url}
                          alt={slot.slot}
                          className="w-20 h-14 object-cover rounded"
                        />
                      ) : (
                        <div className="w-20 h-14 bg-gray-200 rounded flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 capitalize">{slot.slot.replace('_', ' ')}</p>
                        {slot.image && (
                          <p className="text-xs text-gray-500">{slot.image.industry} / {slot.image.image_type}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => openImagePicker(slot.slot)}
                      >
                        {slot.image ? 'Change' : 'Assign'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Slot */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openImagePicker('hero')}
                >
                  + Add Image
                </Button>
              </div>
            </Card>
          )}

          {/* Domain Tab Content */}
          {activeTab === 'domain' && websiteId && (
            <div className="space-y-6">
              <DomainManagement websiteId={websiteId} />
              {/* Only meaningful for a domain the customer brought — a purchased
                  domain is already a zone on our account. */}
              {website.custom_domain && website.domain_source === 'byod' && (
                <DomainDelegation
                  websiteId={websiteId}
                  customDomain={website.custom_domain}
                />
              )}
              <EmailForwardingManager domain={website.custom_domain} />
              <DnsRecordsManager domain={website.custom_domain} />
              <EmailForwardingToggle customerId={website.customer_id} />
            </div>
          )}

          {/* Leads Tab Content */}
          {activeTab === 'leads' && websiteId && <LeadsPanel websiteId={websiteId} />}

          {/* Settings Tab Content — V2 only; `isV4Site` also hides the tab button */}
          {activeTab === 'settings' && websiteId && !isV4Site && (
            <SiteSettings
              websiteId={websiteId}
              onRegenerationQueued={() => {
                // Optionally refresh details after regeneration queued
              }}
            />
          )}

          {/* Onboarding Tab Content */}
          {activeTab === 'onboarding' && details.onboardingSteps && (
            <OnboardingDetails data={details.onboardingSteps} />
          )}
          {activeTab === 'onboarding' && !details.onboardingSteps && (
            <Card>
              <div className="text-center py-8">
                <p className="text-gray-500 mb-2">No onboarding data available</p>
                <p className="text-sm text-gray-400">
                  This customer may have completed onboarding using an older version of the wizard.
                </p>
              </div>
            </Card>
          )}

          {/* Details Tab Content */}
          {activeTab === 'details' && (
            <>
          {/* Tech QA Scores */}
          {techQAResult && (
            <Card>
              <h3 className="font-semibold text-gray-900 mb-4">Tech QA Scores</h3>
              <div className="space-y-3">
                <ScoreBar label="Performance" value={techQAResult.lighthouse_performance ?? 0} />
                <ScoreBar label="Accessibility" value={techQAResult.lighthouse_accessibility ?? 0} />
                <ScoreBar label="SEO" value={techQAResult.lighthouse_seo ?? 0} />
                <ScoreBar label="Best Practices" value={techQAResult.lighthouse_best_practices ?? 0} />
              </div>
              {!techQAResult.passed && techQAResult.failure_reasons.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-2">Failed Checks:</p>
                  <ul className="text-sm text-red-600 list-disc list-inside">
                    {techQAResult.failure_reasons.map((reason: string, i: number) => (
                      <li key={i}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          )}

          {/* Onboarding Info */}
          {onboarding && (
            <Card>
              <h3 className="font-semibold text-gray-900 mb-4">Business Details</h3>
              <dl className="space-y-3 text-sm">
                {onboarding.business_description && (
                  <div>
                    <dt className="text-gray-500">Description</dt>
                    <dd className="text-gray-900">{onboarding.business_description}</dd>
                  </div>
                )}
                {onboarding.services && (
                  <div>
                    <dt className="text-gray-500">Services</dt>
                    <dd className="text-gray-900">{onboarding.services}</dd>
                  </div>
                )}
                {onboarding.primary_color && (
                  <div>
                    <dt className="text-gray-500">Primary Color</dt>
                    <dd className="text-gray-900">{onboarding.primary_color}</dd>
                  </div>
                )}
                {onboarding.secondary_color && (
                  <div>
                    <dt className="text-gray-500">Secondary Color</dt>
                    <dd className="text-gray-900">{onboarding.secondary_color}</dd>
                  </div>
                )}
              </dl>
            </Card>
          )}

          {/* Revision History */}
          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">
              Revision History ({revisions.length})
            </h3>
            {revisions.length > 0 ? (
              <ul className="space-y-3">
                {revisions.map((rev) => (
                  <li key={rev.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 uppercase">
                          {rev.source}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          rev.status === 'completed' ? 'bg-green-100 text-green-700' :
                          rev.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                          rev.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {rev.status}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatRelativeTime(rev.created_at)}
                      </span>
                    </div>
                    {rev.section && (
                      <p className="text-sm font-medium text-gray-900">{rev.section}</p>
                    )}
                    <p className="text-sm text-gray-600 mb-2">{rev.description}</p>
                    {rev.staff_note && (
                      <p className="text-xs text-gray-600 bg-white border border-gray-200 rounded p-2 mb-2">
                        <span className="font-medium">Note to customer:</span> {rev.staff_note}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      {/*
                        "Process with AI" reads the site's files from GitHub.
                        V4 sites have no repo — their content lives in D1 as
                        rawPages/rawSections — so the processor fetches nothing,
                        tells Claude "No files available", and records an error
                        that used to render as a successful-looking change.
                        Hidden until the processor supports the V4 content model.
                      */}
                      {rev.status === 'pending' && !isV4Site && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleProcessRevision(rev.id)}
                          loading={processingRevisionId === rev.id}
                          disabled={processingRevisionId !== null}
                        >
                          Process with AI
                        </Button>
                      )}

                      {/*
                        Manual status controls. Separate from "Process with AI":
                        these record where the work stands so the customer sees
                        movement in their portal — they do not change the site.
                        Only the transitions that make sense from here are shown.
                      */}
                      {rev.status !== 'processing' && rev.status !== 'completed' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleSetRevisionStatus(rev.id, 'processing')}
                          loading={statusRevisionId === rev.id}
                          disabled={statusRevisionId !== null}
                        >
                          Mark in progress
                        </Button>
                      )}
                      {rev.status !== 'completed' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleSetRevisionStatus(rev.id, 'completed')}
                          loading={statusRevisionId === rev.id}
                          disabled={statusRevisionId !== null}
                        >
                          Mark complete
                        </Button>
                      )}
                      {rev.status !== 'rejected' && rev.status !== 'completed' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSetRevisionStatus(rev.id, 'rejected')}
                          loading={statusRevisionId === rev.id}
                          disabled={statusRevisionId !== null}
                        >
                          Decline…
                        </Button>
                      )}
                      {(rev.status === 'completed' || rev.status === 'rejected') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSetRevisionStatus(rev.id, 'pending')}
                          loading={statusRevisionId === rev.id}
                          disabled={statusRevisionId !== null}
                        >
                          Reopen
                        </Button>
                      )}
                    </div>
                    {rev.change_log && rev.change_log.length > 0 && (
                      <div className="mt-2 text-xs">
                        {/*
                          change_log holds either applied changes or a processor
                          error. Rendering file/summary blindly turned an error
                          into an empty bullet under "Changes made" — a failure
                          shown as a success.
                        */}
                        {rev.change_log.some((c) => c.error) ? (
                          <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2">
                            <p className="font-medium">AI processing failed</p>
                            <ul className="list-disc list-inside mt-1">
                              {rev.change_log.filter((c) => c.error).map((c, i) => (
                                <li key={i}>{c.error}</li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <div className="text-gray-500">
                          <p className="font-medium">Changes made:</p>
                          <ul className="list-disc list-inside">
                            {rev.change_log.filter((c) => c.file || c.summary).map((change, i) => (
                              <li key={i}>{change.file}: {change.summary}</li>
                            ))}
                          </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">No revisions yet</p>
            )}
          </Card>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Overlay when panel is hovering (not pinned open) */}
      {!isPanelOpen && isPanelHovering && (
        <div className="fixed inset-0 bg-black/10 pointer-events-none" style={{ zIndex: 9997 }} />
      )}

      {/* Image Picker Modal */}
      <Modal
        isOpen={!!showImagePicker}
        onClose={() => {
          setShowImagePicker(null);
          setPickerImages([]);
        }}
        title={`Select Image for ${showImagePicker?.replace('_', ' ')}`}
        size="xl"
      >
        <div className="space-y-4">
          {/* Filter Controls */}
          <div className="flex gap-4 pb-4 border-b border-gray-200">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Industry
              </label>
              <select
                value={pickerIndustry}
                onChange={(e) => setPickerIndustry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Industries</option>
                {INDUSTRIES.map((ind) => (
                  <option key={ind.value} value={ind.value}>
                    {ind.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image Type
              </label>
              <select
                value={pickerImageType}
                onChange={(e) => setPickerImageType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Types</option>
                {IMAGE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Image Grid */}
          <div className="max-h-[400px] overflow-y-auto">
            {pickerLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : pickerImages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-2">
                  No approved images match your filters.
                </p>
                <p className="text-sm text-gray-400">
                  Try adjusting the industry or image type filters, or add more images in the Image Library.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-3">
                  {pickerImages.length} image{pickerImages.length !== 1 ? 's' : ''} found
                </p>
                <div className="grid grid-cols-4 gap-3">
                  {pickerImages.map((img) => (
                    <button
                      key={img.id}
                      onClick={() => handleAssignImage(img.id)}
                      className="relative group rounded-lg overflow-hidden border-2 border-transparent hover:border-primary-500 transition-all"
                    >
                      <img
                        src={img.r2_url}
                        alt={img.original_query || 'Stock image'}
                        className="w-full aspect-video object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-sm font-medium">Select</span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                        <p className="text-white text-xs truncate capitalize">
                          {img.image_type.replace('_', ' ')}
                        </p>
                        <p className="text-white/70 text-xs truncate">
                          {img.industry}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Approve Modal */}
      <ConfirmModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={handleApprove}
        title="Send for Customer Review"
        message="This will publish the latest version and send the customer a review email with a magic link. Are you sure the site is ready?"
        confirmText="Send for Review"
        loading={actionLoading}
      />

      {/* Make Live Modal */}
      <ConfirmModal
        isOpen={showMakeLiveModal}
        onClose={() => setShowMakeLiveModal(false)}
        onConfirm={handleMakeLive}
        title="Make Site Live"
        message="This will deploy the site to production and mark it as live. Any unpublished edits will be published first."
        confirmText="Make Live"
        loading={actionLoading}
      />

      {/* Launch Now Modal (admin override — skips customer approval) */}
      <ConfirmModal
        isOpen={showLaunchNowModal}
        onClose={() => setShowLaunchNowModal(false)}
        onConfirm={handleLaunchNow}
        title="Launch Now — skip customer approval"
        message={
          website.status === 'needs-customer-revision'
            ? `⚠️ This customer REQUESTED CHANGES that haven't been published. Launching now publishes the current version as-is, ignores their revision request, and notifies them the site is live${website.custom_domain ? ` on ${website.custom_domain}` : ''}. Only do this if you've reviewed the site and decided not to make the requested changes. Continue?`
            : `This publishes ${website.business_name?.trim() || 'this site'} live${website.custom_domain ? ` on ${website.custom_domain}` : ''} WITHOUT waiting for the customer to approve, and notifies the customer that their site is live. Only do this if you've reviewed the site and the customer is unresponsive. Continue?`
        }
        confirmText="Launch Now"
        confirmVariant="danger"
        loading={actionLoading}
      />

      {/* Revision Modal */}
      <Modal
        isOpen={showRevisionModal}
        onClose={() => setShowRevisionModal(false)}
        title="Request Revision"
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowRevisionModal(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRevision}
              loading={actionLoading}
              disabled={!revisionDescription.trim()}
            >
              Submit Request
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Section"
            options={[{ value: '', label: 'Select a section...' }, ...SECTION_OPTIONS]}
            value={revisionSection}
            onChange={(e) => setRevisionSection(e.target.value)}
          />
          <Textarea
            label="Description"
            placeholder="Describe the changes needed..."
            value={revisionDescription}
            onChange={(e) => setRevisionDescription(e.target.value)}
            rows={4}
          />
        </div>
      </Modal>

    </PageContainer>
  );
}

// Score bar component
function ScoreBar({ label, value }: { label: string; value: number }) {
  const getColor = (val: number) => {
    if (val >= 90) return 'bg-green-500';
    if (val >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium">{value}</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${getColor(value)} transition-all`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}
